import { shallowEqual, useSelector } from "react-redux";
import { selectSession, selectTransformedGrants } from "../app/sessionSlice";
import { AvataxRole } from "../features/navigation/Subscription";

import { matchRole } from "../shared/Utils";
import type { Feature, UseAccessibleReturn } from "./types";
import { useIsIAM } from "./useAccessControl";

/**
 * Custom hook to determine whether a user has certain permission(s)
 * based on transformed grant data from Redux state.
 *
 * @returns {UseAccessibleReturn} Object containing the hasIAMAccess and hasNonIAMAccess functions
 */
export const useAccessible = (): UseAccessibleReturn => {
    // Get transformed grant permissions from Redux
    const grants = useSelector(selectTransformedGrants) || {};
    const session = useSelector(selectSession, shallowEqual);
    const isIAM = useIsIAM();

    /**
     * Determines whether the user has the specified permission(s).
     *
     * @param {Feature} feature - Configuration object.
     * @returns {boolean} `true` if the user has the required permission(s), otherwise `false`.
     */
    const hasIAMAccess = (feature: Feature): boolean => {
        if (!isIAM) return false;

        // Handle permissions property - exists in both FeatureFlagConfig and AccessConfig
        const requiredPermissions = feature.permissions;
        const requireAnyPermission =
            "requireAnyPermission" in feature ? feature.requireAnyPermission : false;

        const isEligible = (permission: string): boolean => Boolean(grants[permission]);

        // Case 1: No permission required
        if (!requiredPermissions) {
            return true;
        }

        // Case 2: Array of permissions
        if (Array.isArray(requiredPermissions)) {
            if (requiredPermissions.length === 0) {
                return true;
            }

            const checkMethod = requireAnyPermission ? "some" : "every";
            return requiredPermissions[checkMethod](isEligible);
        }

        // Case 3: Single permission
        return isEligible(requiredPermissions);
    };

    /**
     * Determines whether the user has non-IAM access to a feature
     *
     * @param {Feature} feature - Configuration object
     * @returns {boolean} `true` if the user has non-IAM access, otherwise `false`
     */
    const hasNonIAMAccess = (feature: Feature): boolean => {
        if (isIAM) return false;

        // Handle ecmRole property - exists in FeatureFlagConfig but not AccessConfig
        const featureEcmRole = "ecmRole" in feature ? feature.ecmRole : undefined;
        // Handle avataxRole property - exists in FeatureFlagConfig but not AccessConfig
        const featureAvataxRole = "avataxRole" in feature ? feature.avataxRole : undefined;
        // Handle checkAdmin property - exists in FeatureFlagConfig but not AccessConfig
        const checkAdmin = "checkAdmin" in feature ? feature.checkAdmin : false;

        if (
            session.role === AvataxRole.ECMRoleUser &&
            featureEcmRole?.length &&
            session?.ecmRole?.length
        )
            return matchRole(featureEcmRole, session.ecmRole);

        if (
            session.role === AvataxRole.ECMAccountUser ||
            session.role === AvataxRole.ECMCompanyUser
        )
            return Boolean(featureAvataxRole?.includes(session.role));

        if (checkAdmin) return Boolean(session?.role?.includes("Admin"));
        return true;
    };

    return { hasIAMAccess, hasNonIAMAccess };
};

// Example usage:
// const { hasIAMAccess } = useAccessible();
// if (hasIAMAccess({permissions: 'EDIT_PROFILE'})) { ... }
// if (hasIAMAccess({permissions: ['VIEW_PROFILE', 'EDIT_PROFILE']})) { ... }
